/**
 * FahrzeugDetailDrawer — Shell für die Fahrzeug-Detailansicht.
 *
 * Desktop: Slide-in von rechts, ca. 40% Viewport-Breite.
 * Mobile: Fullscreen mit Swipe-nach-rechts-to-close.
 * Tab-Navigation mit allen 5 Tabs registriert; Stammdaten ist in diesem
 * Issue (#185) implementiert, andere Tabs zeigen Placeholder bzw. nutzen
 * die bereits existierenden Tab-Komponenten aus #186.
 *
 * CI-Akzent wird aus `fahrzeug.betrieb` abgeleitet (LCS blau / SV türkis).
 *
 * Part of Issue #185 / Parent #183.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { X } from 'lucide-react';
import { resolveStammdatenCi } from '@/lib/stammdaten-tab';
import StammdatenTab from './StammdatenTab';
import FinanzenTab from './FinanzenTab';
import MietvertraegeTab from './MietvertraegeTab';

export type FahrzeugDrawerTabId =
  | 'stammdaten'
  | 'finanzen'
  | 'dokumente'
  | 'historie'
  | 'mietvertraege';

export interface FahrzeugDrawerVehicle {
  id: string;
  marke?: string | null;
  modell?: string | null;
  variante?: string | null;
  kennzeichen?: string | null;
  betrieb?: string | null;
  /** Synonym für `betrieb` — wir akzeptieren beide Feldnamen. */
  business_unit?: string | null;
}

interface FahrzeugDetailDrawerProps {
  fahrzeug: FahrzeugDrawerVehicle | null;
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

const TABS: { id: FahrzeugDrawerTabId; label: string }[] = [
  { id: 'stammdaten', label: 'Stammdaten' },
  { id: 'finanzen', label: 'Finanzen' },
  { id: 'dokumente', label: 'Dokumente' },
  { id: 'historie', label: 'Historie' },
  { id: 'mietvertraege', label: 'Mietverträge' },
];

const SWIPE_CLOSE_THRESHOLD_PX = 100;

export default function FahrzeugDetailDrawer({
  fahrzeug,
  open,
  onClose,
  onChanged,
}: FahrzeugDetailDrawerProps) {
  const [mounted, setMounted] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [tab, setTab] = useState<FahrzeugDrawerTabId>('stammdaten');
  const [dragOffset, setDragOffset] = useState(0);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      document.body.style.overflow = 'hidden';
      const handle = requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
      return () => {
        cancelAnimationFrame(handle);
      };
    }
    setAnimating(false);
    const timer = setTimeout(() => {
      setMounted(false);
      setTab('stammdaten');
      setDragOffset(0);
      document.body.style.overflow = '';
    }, 260);
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
    };
  }, [open]);

  const ci = useMemo(
    () => resolveStammdatenCi(fahrzeug?.business_unit ?? fahrzeug?.betrieb),
    [fahrzeug?.business_unit, fahrzeug?.betrieb],
  );

  if (!mounted || !fahrzeug) return null;

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'touch') return;
    pointerStart.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointerStart.current) return;
    const dx = e.clientX - pointerStart.current.x;
    const dy = e.clientY - pointerStart.current.y;
    if (Math.abs(dy) > Math.abs(dx)) return;
    setDragOffset(Math.max(0, dx));
  };

  const onPointerEnd = () => {
    if (dragOffset > SWIPE_CLOSE_THRESHOLD_PX) {
      pointerStart.current = null;
      setDragOffset(0);
      onClose();
      return;
    }
    pointerStart.current = null;
    setDragOffset(0);
  };

  const title = [fahrzeug.marke, fahrzeug.modell].filter(Boolean).join(' ').trim()
    || 'Fahrzeug';
  const subtitle = [fahrzeug.variante, fahrzeug.kennzeichen]
    .filter((s): s is string => !!s && s.trim().length > 0)
    .join(' · ');

  const panelTransform = animating
    ? `translateX(${dragOffset}px)`
    : 'translateX(100%)';

  return (
    <div
      className="fixed inset-0 z-[70]"
      role="dialog"
      aria-modal="true"
      aria-label={`Fahrzeugdetails ${title}`}
      data-testid="fahrzeug-drawer"
    >
      <button
        type="button"
        aria-label="Drawer schließen"
        onClick={onClose}
        className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-200 ${
          animating ? 'opacity-100' : 'opacity-0'
        }`}
        data-testid="fahrzeug-drawer-backdrop"
      />
      <div
        className="absolute inset-y-0 right-0 bg-white shadow-2xl flex flex-col w-full md:w-[40vw] md:min-w-[520px] md:max-w-[760px] transition-transform duration-200 ease-out"
        style={{
          transform: panelTransform,
          fontFamily: ci.fontFamily,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        data-testid="fahrzeug-drawer-panel"
      >
        <div
          className="flex items-start justify-between px-5 py-4 border-b border-gray-100"
          style={{ borderTop: `3px solid ${ci.accent}` }}
        >
          <div className="min-w-0">
            <h2
              className="text-base font-bold text-gray-900 truncate"
              data-testid="fahrzeug-drawer-title"
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-[11px] text-gray-400 mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            aria-label="Schließen"
            data-testid="fahrzeug-drawer-close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <nav
          className="flex gap-1 px-3 pt-2 border-b border-gray-100 overflow-x-auto"
          role="tablist"
          data-testid="fahrzeug-drawer-tabs"
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className="relative px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors"
                style={{
                  color: active ? ci.accent : '#6B7280',
                }}
                data-testid={`fahrzeug-drawer-tab-${t.id}`}
              >
                {t.label}
                <span
                  className="absolute left-3 right-3 -bottom-[1px] h-[2px] rounded-full transition-opacity"
                  style={{
                    backgroundColor: ci.accent,
                    opacity: active ? 1 : 0,
                  }}
                />
              </button>
            );
          })}
        </nav>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'stammdaten' && (
            <StammdatenTab
              fahrzeug={{ id: fahrzeug.id }}
              accent={ci.accent}
              onKmUpdated={onChanged}
            />
          )}
          {tab === 'finanzen' && <FinanzenTab fahrzeug={{ id: fahrzeug.id }} />}
          {tab === 'dokumente' && <PlaceholderTab label="Dokumente" />}
          {tab === 'historie' && <PlaceholderTab label="Historie" />}
          {tab === 'mietvertraege' && (
            <MietvertraegeTab fahrzeug={{ id: fahrzeug.id }} />
          )}
        </div>
      </div>
    </div>
  );
}

function PlaceholderTab({ label }: { label: string }) {
  return (
    <div
      className="text-center py-12 text-sm text-gray-400"
      data-testid={`fahrzeug-drawer-placeholder-${label.toLowerCase()}`}
    >
      {label} folgt in einem separaten Issue
    </div>
  );
}
